import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageLoader } from 'shared/components/ui/BrandLoader';
import axiosInstance from 'shared/services/api/axiosInstance';
import { formatRelativeTime, formatDate } from 'features/agent-analytics/components/runCells';
import FailureTags from 'features/agent-analytics/components/failures/FailureTags';
import { StatusTag, CategoryTag } from 'features/issues/components/IssueTags';

/** GET /api/failures/signals — the issues this is evidence for, and what failed in it. */
export async function getTargetSignals(teamId, kind, id) {
  const { data } = await axiosInstance.get('/failures/signals', { params: { team_id: teamId, kind, id } });
  return { issues: data?.issues || [], failures: data?.failures || [] };
}

function Heading({ children, count }) {
  return (
    <div className="mb-1.5 flex items-baseline gap-1.5 text-[11px] font-medium uppercase tracking-wide text-[color:var(--text-muted)]">
      {children}
      {count ? <span className="tabular-nums">{count}</span> : null}
    </div>
  );
}

const rowCls = 'flex w-full min-w-0 flex-col gap-1 px-2.5 py-2 text-left transition-colors hover:bg-surface-hover';

function When({ at }) {
  return (
    <span className="shrink-0 text-[11px] tabular-nums text-[color:var(--text-muted)]" title={formatDate(at)}>
      {formatRelativeTime(at)}
    </span>
  );
}

/**
 * The Signals tab of a run, span or conversation (TRO-488): the issues it is
 * evidence for — a row opens the issue — and what failed in it, by the
 * Failures tab's rules — a row opens the span.
 */
export default function TargetSignals({ teamId, kind, id, onCount, onSpanClick, onRunClick }) {
  const navigate = useNavigate();
  const { team: teamSlug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!teamId || !id) return undefined;
    let live = true;
    setData(null);
    setError(null);
    getTargetSignals(teamId, kind, id)
      .then((d) => { if (!live) return; setData(d); onCount?.(d.issues.length + d.failures.length); })
      .catch(() => { if (!live) return; setError('Could not load signals.'); onCount?.(0); });
    return () => { live = false; };
  }, [teamId, kind, id, onCount]);

  if (error) return <div className="text-[12px] text-[color:var(--feedback-error)]">{error}</div>;
  if (!data) return <PageLoader label="Loading signals" />;
  if (!data.issues.length && !data.failures.length) {
    return <div className="text-[12px] text-[color:var(--text-muted)]">No issues or failures.</div>;
  }

  const openIssue = (issueId) => navigate(`/${encodeURIComponent(teamSlug || '')}/agent-analytics/issues?issue=${encodeURIComponent(issueId)}`);
  const openFailure = (f) => {
    if (f.span_id && onSpanClick) onSpanClick(f.span_id);
    else if (f.run_id && onRunClick && kind !== 'run') onRunClick(f.run_id);
  };

  return (
    <div className="flex flex-col gap-4">
      {data.issues.length ? (
        <section>
          <Heading count={data.issues.length}>Issues</Heading>
          <div className="divide-y divide-[color:var(--border-subtle)] overflow-hidden rounded-md border border-[color:var(--border-default)]">
            {data.issues.map((i) => (
              <button key={i.id} type="button" onClick={() => openIssue(i.id)} className={rowCls}>
                <span className="flex min-w-0 items-baseline gap-2">
                  <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-[color:var(--text-primary)]" title={i.title}>{i.title}</span>
                  <When at={i.last_at} />
                </span>
                <span className="flex flex-wrap items-center gap-1">
                  <StatusTag status={i.status} />
                  <CategoryTag category={i.category} />
                </span>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {data.failures.length ? (
        <section>
          <Heading count={data.failures.length}>Failures</Heading>
          <div className="divide-y divide-[color:var(--border-subtle)] overflow-hidden rounded-md border border-[color:var(--border-default)]">
            {data.failures.map((f) => {
              const clickable = Boolean((f.span_id && onSpanClick) || (f.run_id && onRunClick && kind !== 'run'));
              const Row = clickable ? 'button' : 'div';
              return (
                <Row
                  key={`${f.span_id || f.run_id}`}
                  {...(clickable ? { type: 'button', onClick: () => openFailure(f) } : {})}
                  className={clickable ? rowCls : rowCls.replace(' transition-colors hover:bg-surface-hover', '')}
                >
                  <span className="flex min-w-0 items-baseline gap-2">
                    <span className="min-w-0 flex-1 truncate text-[12px] text-[color:var(--text-primary)]" title={f.error_message || ''}>
                      {f.error_message || f.error_type || 'Error'}
                    </span>
                    <When at={f.started_at} />
                  </span>
                  <span className="flex min-w-0 flex-wrap items-center gap-1">
                    <span className="mr-1 max-w-full truncate text-[11px] text-[color:var(--text-muted)]">{f.name}</span>
                    <FailureTags cluster={f} statusCode={f.status_code} />
                  </span>
                </Row>
              );
            })}
          </div>
        </section>
      ) : null}
    </div>
  );
}
